import { useMemo } from 'react';
import { useWebsiteV2Feature } from '@shared/hooks/useWebsiteV2Feature';

export type WebsiteV2GuestGate = Readonly<{
  /** The event carries `useWebsiteV2`: its guest site is owned by carbon, never by charm. */
  isWebsiteV2: boolean;
  /** True until the flag has resolved. */
  isLoading: boolean;
}>;

/**
 * Guest-side half of the "flag on ⇒ charm never shows site content" rule (doc/website-v2/PLAN.md).
 *
 * For a `useWebsiteV2` event the server proxies the carbon document whenever a published carbon
 * site exists, and answers 503 when that proxy fails. So the only way charm is rendering a flagged
 * event outside a preview is that the server found NO published site — which makes "flagged and
 * charm is here" the whole answer, with no round trip: the site is unpublished, and charm must
 * show its "Site Under Construction" gate — even to admins, and regardless of the V1
 * `websiteState` — because the V1 content the couple left behind must not leak out through the
 * old renderer.
 *
 * Deliberately NOT a carbon lookup (removed 2026-09-09): a query could be defeated by a gateway
 * hiccup and cost every unpublished-site guest a request, while the inference cannot fail open.
 * When event_service exposes the event's active site on the `event` graph object, the server's
 * `resolveCarbonGuestSiteId` is the single swap point; this hook does not change.
 */
export const useWebsiteV2GuestGate = (): WebsiteV2GuestGate => {
  const { value: isWebsiteV2, isLoading } = useWebsiteV2Feature();
  return useMemo(() => ({ isWebsiteV2: !isLoading && isWebsiteV2, isLoading }), [isWebsiteV2, isLoading]);
};
